import { useState, FormEvent } from 'react';

const ContactSection = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    
    if (!formData.name || !formData.email || !formData.message) {
      setSubmitStatus('error');
      return;
    }

    if (formData.message.length > 500) {
      setSubmitStatus('error');
      return;
    }

    setIsSubmitting(true);
    setSubmitStatus('idle');

    try {
      const formBody = new URLSearchParams();
      formBody.append('name', formData.name);
      formBody.append('email', formData.email);
      formBody.append('message', formData.message);

      const response = await fetch('https://readdy.ai/api/form/d6anmhea728k8ctu3dtg', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: formBody.toString()
      });

      if (response.ok) {
        setSubmitStatus('success');
        setFormData({ name: '', email: '', message: '' });
      } else {
        setSubmitStatus('error');
      }
    } catch (error) {
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section id="contact" className="py-24 lg:py-32 bg-slate-900 relative overflow-hidden">
      <div className="absolute inset-0 opacity-5">
        <div className="absolute inset-0" style={{
          backgroundImage: 'radial-gradient(circle, rgba(255, 255, 255, 0.1) 1px, transparent 1px)',
          backgroundSize: '30px 30px'
        }}></div>
      </div>

      <div className="relative z-10 max-w-7xl mx-auto px-6 lg:px-12">
        <div className="grid lg:grid-cols-3 gap-16">
          <div className="space-y-6">
            <div className="flex items-center space-x-2">
              <h3 className="font-['Playfair_Display'] font-bold text-2xl text-white">CANDOR d.o.o.</h3>
              <div className="w-2 h-2 bg-cyan-400 rounded-full"></div>
            </div>
            <p className="text-white/60 text-base font-light">Modern iOS Applications</p>
          </div>

          <div className="space-y-6">
            <h4 className="text-sm uppercase tracking-wider text-white/50 font-medium">Location</h4>
            <div className="space-y-3 text-white/85 text-base">
              <p className="flex items-start">
                <i className="ri-map-pin-line text-lg mr-3 mt-0.5 text-cyan-400 flex-shrink-0 w-5 h-5 flex items-center justify-center"></i>
                <span>Augusta Senoe 89</span>
              </p>
              <p className="flex items-start">
                <i className="ri-building-line text-lg mr-3 mt-0.5 text-cyan-400 flex-shrink-0 w-5 h-5 flex items-center justify-center"></i>
                <span>City Valpovo</span>
              </p>
              <p className="flex items-start">
                <i className="ri-mail-line text-lg mr-3 mt-0.5 text-cyan-400 flex-shrink-0 w-5 h-5 flex items-center justify-center"></i>
                <span>Zip 31550</span>
              </p>
              <p className="flex items-start">
                <i className="ri-earth-line text-lg mr-3 mt-0.5 text-cyan-400 flex-shrink-0 w-5 h-5 flex items-center justify-center"></i>
                <span>Country Croatia</span>
              </p>
            </div>
          </div>

          <div className="space-y-6">
            <h4 className="text-sm uppercase tracking-wider text-white/50 font-medium">Contact</h4>
            <div className="space-y-5">
              <div>
                <p className="text-white/50 text-sm mb-1 flex items-center">
                  <i className="ri-mail-send-line text-base mr-2 text-cyan-400 w-5 h-5 flex items-center justify-center"></i>
                  <span>Email</span>
                </p>
                <a href="mailto:support@candordoo.com" className="text-white/85 text-base hover:text-cyan-400 transition-colors cursor-pointer">
                  support@candordoo.com
                </a>
              </div>
              <div>
                <p className="text-white/50 text-sm mb-1 flex items-center">
                  <i className="ri-phone-line text-base mr-2 text-cyan-400 w-5 h-5 flex items-center justify-center"></i>
                  <span>Number</span>
                </p>
                <a href="tel:+16206590786" className="text-white/85 text-base hover:text-cyan-400 transition-colors cursor-pointer">
                  +1 6206590786
                </a>
              </div>
            </div>
          </div>
        </div>

        <div className="mt-20 pt-12 border-t border-white/10">
          <div className="max-w-2xl mx-auto">
            <h3 className="font-['Inter'] font-bold text-3xl text-white text-center mb-8">Get In Touch</h3>
            
            <form id="contact-form" onSubmit={handleSubmit} data-readdy-form className="space-y-6">
              <div>
                <label htmlFor="name" className="block text-white/70 text-sm mb-2">Name</label>
                <input
                  type="text"
                  id="name"
                  name="name"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-lg text-white text-sm focus:outline-none focus:border-cyan-400 transition-colors"
                  required
                />
              </div>
              
              <div>
                <label htmlFor="email" className="block text-white/70 text-sm mb-2">Email</label>
                <input
                  type="email"
                  id="email"
                  name="email"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-lg text-white text-sm focus:outline-none focus:border-cyan-400 transition-colors"
                  required
                />
              </div>
              
              <div>
                <label htmlFor="message" className="block text-white/70 text-sm mb-2">Message</label>
                <textarea
                  id="message"
                  name="message"
                  value={formData.message}
                  onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                  maxLength={500}
                  rows={5}
                  className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-lg text-white text-sm focus:outline-none focus:border-cyan-400 transition-colors resize-none"
                  required
                ></textarea>
                <p className="text-white/40 text-xs mt-1">{formData.message.length}/500 characters</p>
              </div>

              <button
                type="submit"
                disabled={isSubmitting}
                className="w-full bg-gradient-to-r from-cyan-500 to-blue-500 text-white font-medium py-3 px-6 rounded-lg hover:from-cyan-600 hover:to-blue-600 transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer whitespace-nowrap"
              >
                {isSubmitting ? 'Sending...' : 'Send Message'}
              </button>

              {submitStatus === 'success' && (
                <div className="bg-emerald-500/20 border border-emerald-500/50 text-emerald-300 px-4 py-3 rounded-lg text-sm text-center">
                  Message sent successfully! We'll get back to you soon.
                </div>
              )}

              {submitStatus === 'error' && (
                <div className="bg-red-500/20 border border-red-500/50 text-red-300 px-4 py-3 rounded-lg text-sm text-center">
                  Failed to send message. Please try again or contact us directly.
                </div>
              )}
            </form>
          </div>
        </div>

        <div className="mt-16 pt-8 border-t border-white/10 text-center">
          <p className="text-white/40 text-sm">
            © 2025 CANDOR d.o.o. All rights reserved. 
            <a href="https://readdy.ai/?ref=logo" target="_blank" rel="noopener noreferrer" className="ml-2 text-cyan-400 hover:text-cyan-300 transition-colors cursor-pointer whitespace-nowrap">
              Powered by Readdy
            </a>
          </p>
        </div>
      </div>
    </section>
  );
};

export default ContactSection;
